import { useState, useEffect, useRef } from 'react';
import { Zap, MapPin, Battery, Filter, Search, Navigation, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVCharging = () => {
  const [stations, setStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    document.title = 'EV Charging Stations South Africa | Find Chargers Near You | AutoSearch SA';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Find EV charging stations across South Africa. GridCars, Tesla Superchargers, Shell Recharge & more. Locate chargers in Johannesburg, Cape Town, Durban.');
    
    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    
    // Load Leaflet JS
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => fetchStations();
      document.body.appendChild(script);
    } else {
      fetchStations();
    }
  }, []);

  useEffect(() => {
    filterStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince, selectedNetwork, stations]);

  useEffect(() => {
    if (filteredStations.length > 0 && window.L && mapRef.current) {
      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredStations]);

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API}/ev-charging-stations`);
      setStations(res.data.stations || []);
      setFilteredStations(res.data.stations || []);
      setProvinces(res.data.provinces || []);
      setNetworks(res.data.networks || []);
    } catch (e) {
      console.error('Failed to fetch stations:', e);
      // Fallback data if API fails
      const fallbackStations = [
        {id: 1, name: "GridCars Sandton City", address: "Sandton City Mall", province: "Gauteng", lat: -26.1076, lng: 28.0567, charger_type: "DC Fast", network: "GridCars", kw: 60},
        {id: 2, name: "Tesla Supercharger Fourways", address: "Fourways Mall", province: "Gauteng", lat: -26.0186, lng: 28.0127, charger_type: "Supercharger", network: "Tesla", kw: 250},
        {id: 3, name: "GridCars V&A Waterfront", address: "V&A Waterfront, Cape Town", province: "Western Cape", lat: -33.9035, lng: 18.4206, charger_type: "DC Fast", network: "GridCars", kw: 60},
      ];
      setStations(fallbackStations);
      setFilteredStations(fallbackStations);
      setProvinces(['Gauteng', 'Western Cape']);
      setNetworks(['GridCars', 'Tesla']);
    } finally {
      setLoading(false);
    }
  };

  const filterStations = () => {
    let filtered = [...stations];
    if (selectedProvince) {
      filtered = filtered.filter(s => s.province === selectedProvince);
    }
    if (selectedNetwork) {
      filtered = filtered.filter(s => s.network === selectedNetwork);
    }
    setFilteredStations(filtered);
  };

  const initMap = () => {
    if (!window.L || !mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Initialize or update map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = window.L.map(mapRef.current).setView([-28.4793, 24.6727], 5);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(mapInstanceRef.current);
    }

    // Custom icon
    const evIcon = window.L.divIcon({
      className: 'custom-ev-marker',
      html: `<div style="background: #10b981; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
        </svg>
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });

    // Add markers
    filteredStations.forEach(station => {
      const marker = window.L.marker([station.lat, station.lng], { icon: evIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(`
          <div style="min-width: 200px;">
            <h3 style="font-weight: bold; margin-bottom: 4px;">${station.name}</h3>
            <p style="color: #666; font-size: 12px; margin-bottom: 4px;">${station.address}</p>
            <p style="font-size: 12px;"><strong>Network:</strong> ${station.network}</p>
            <p style="font-size: 12px;"><strong>Power:</strong> ${station.kw}kW ${station.charger_type}</p>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lng}" 
               target="_blank" 
               style="display: inline-block; margin-top: 8px; padding: 4px 8px; background: #10b981; color: white; border-radius: 4px; text-decoration: none; font-size: 12px;">
              Get Directions
            </a>
          </div>
        `);
      markersRef.current.push(marker);
    });

    // Fit bounds
    if (filteredStations.length > 0) {
      const bounds = window.L.latLngBounds(filteredStations.map(s => [s.lat, s.lng]));
      mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });
    }
  };

  const focusStation = (station) => {
    setSelectedStation(station);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([station.lat, station.lng], 14);
      // Find and open popup
      markersRef.current.forEach(m => {
        if (m.getLatLng().lat === station.lat && m.getLatLng().lng === station.lng) {
          m.openPopup();
        }
      });
    }
  };

  const getNetworkColor = (network) => {
    const colors = {
      'GridCars': 'bg-blue-500',
      'Tesla': 'bg-red-500',
      'Shell Recharge': 'bg-yellow-500',
      'BMW': 'bg-blue-700',
      'Jaguar': 'bg-green-700'
    };
    return colors[network] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-12">
        <div className="container">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-10 h-10 text-yellow-300" />
            <h1 className="text-3xl md:text-4xl font-bold">EV Charging Stations</h1>
          </div>
          <p className="text-lg text-green-100 max-w-2xl">
            Find electric vehicle charging stations across South Africa. {stations.length} stations from GridCars, Tesla, Shell Recharge & more.
          </p>
        </div>
      </section>

      <div className="container py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">{stations.length}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Stations</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{networks.length}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Networks</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-purple-600">{provinces.length}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Provinces</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-amber-600">250kW</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Max Power</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Provinces</option>
              {provinces.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={selectedNetwork}
              onChange={(e) => setSelectedNetwork(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Networks</option>
              {networks.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredStations.length} charging stations
          </div>
        </div>

        {/* Map & List */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Map */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-600" />
                Station Map
              </h2>
            </div>
            {loading ? (
              <div className="h-96 flex items-center justify-center">
                <div className="spinner"></div>
              </div>
            ) : (
              <div ref={mapRef} className="h-96 w-full" style={{ minHeight: '400px' }}></div>
            )}
          </div>

          {/* Station List */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Battery className="w-5 h-5 text-green-600" />
                Station List
              </h2>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {filteredStations.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  No stations found. Try adjusting your filters.
                </div>
              ) : (
                filteredStations.map(station => (
                  <div
                    key={station.id}
                    onClick={() => focusStation(station)}
                    className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                      selectedStation?.id === station.id ? 'bg-green-50 dark:bg-green-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{station.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{station.address}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-xs text-white rounded-full ${getNetworkColor(station.network)}`}>
                            {station.network}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {station.kw}kW • {station.charger_type}
                          </span>
                        </div>
                      </div>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 bg-green-100 dark:bg-green-900 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition"
                      >
                        <Navigation className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Network Legend */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <h3 className="font-bold text-gray-900 dark:text-white mb-3">Charging Networks</h3>
          <div className="flex flex-wrap gap-3">
            {networks.map(network => (
              <div key={network} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getNetworkColor(network)}`}></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{network}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EVCharging;
